import React, { useState, useEffect, createContext, useContext } from 'react';
import { Menu, X, Phone, Moon, Sun } from 'lucide-react';

// Theme Context
const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('yescas-theme');
    return saved === 'dark' ? true : false; // Default to light mode
  });

  useEffect(() => {
    localStorage.setItem('yescas-theme', isDark ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  const toggleTheme = () => setIsDark(!isDark);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('inicio');
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { id: 'inicio', label: 'Inicio' },
    { id: 'nosotros', label: 'Sobre nosotros' },
    { id: 'servicios', label: 'Servicios' },
    { id: 'proyectos', label: 'Proyectos' },
    { id: 'contacto', label: 'Contacto' },
  ];

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(id);
      setMenuOpen(false);
    }
  };

  return (
    <>
      <nav
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          padding: scrolled ? '0.6rem 2rem' : '1rem 2rem',
          background: isDark 
            ? (scrolled ? 'rgba(17,17,17,0.97)' : 'rgba(17,17,17,0.95)')
            : (scrolled ? 'rgba(255,255,255,0.97)' : 'rgba(255,255,255,0.95)'),
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: isDark 
            ? '1px solid rgba(255,255,255,0.06)' 
            : '1px solid rgba(0,0,0,0.06)',
          boxShadow: scrolled 
            ? (isDark ? '0 2px 20px rgba(0,0,0,0.3)' : '0 2px 20px rgba(0,0,0,0.06)')
            : 'none',
          transition: 'padding 0.4s ease, background 0.4s ease, box-shadow 0.4s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {/* Logo */}
        <button
          onClick={() => scrollTo('inicio')}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            padding: 0,
          }}
        >
          <img
            src={isDark ? '/logo-dark.svg' : '/logo.svg'}
            alt="Grupo Yescas"
            style={{
              height: 56,
              width: 'auto',
            }}
          />
        </button>

        {/* Desktop Nav */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
        }} className="desktop-nav">
          {navLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => scrollTo(link.id)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: 'var(--font-heading)',
                fontWeight: 500,
                fontSize: '0.8rem',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: activeSection === link.id 
                  ? 'var(--red)' 
                  : (isDark ? 'rgba(255,255,255,0.85)' : 'var(--dark)'),
                padding: '0.5rem 0.875rem',
                borderRadius: 4,
                position: 'relative',
                transition: 'color 0.3s ease',
              }}
              onMouseEnter={(e) => {
                if (activeSection !== link.id) e.target.style.color = 'var(--red)';
              }}
              onMouseLeave={(e) => {
                if (activeSection !== link.id) e.target.style.color = isDark ? 'rgba(255,255,255,0.85)' : 'var(--dark)';
              }}
            >
              {link.label}
              {activeSection === link.id && (
                <span style={{
                  position: 'absolute',
                  bottom: 2,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 20,
                  height: 2,
                  background: 'var(--red)',
                  borderRadius: 2,
                }} />
              )}
            </button>
          ))}

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 38,
              height: 38,
              background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
              border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
              borderRadius: 8,
              cursor: 'pointer',
              color: isDark ? 'rgba(255,255,255,0.8)' : 'var(--dark)',
              transition: 'background 0.3s ease, transform 0.2s ease',
              marginLeft: '0.5rem',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--red)';
              e.currentTarget.style.color = 'white';
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)';
              e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.8)' : 'var(--dark)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            title={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          <a
            href="tel:+529514997959"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'var(--red)',
              color: 'white',
              fontFamily: 'var(--font-heading)',
              fontWeight: 700,
              fontSize: '0.75rem',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              padding: '0.6rem 1.25rem',
              borderRadius: 4,
              textDecoration: 'none',
              marginLeft: '0.5rem',
              transition: 'background 0.3s ease, transform 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--red-dark)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--red)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <Phone size={13} />
            Contactar
          </a>
        </div>

        {/* Mobile Hamburger */}
        <div style={{ display: 'none', alignItems: 'center', gap: '0.5rem' }} className="mobile-menu-btns">
          <button
            onClick={toggleTheme}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 38,
              height: 38,
              background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
              border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
              borderRadius: 8,
              cursor: 'pointer',
              color: isDark ? 'rgba(255,255,255,0.8)' : 'var(--dark)',
            }}
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            style={{
              background: 'none',
              border: isDark ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.15)',
              borderRadius: 6,
              padding: '0.4rem',
              cursor: 'pointer',
              color: isDark ? 'white' : 'var(--dark)',
            }}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: isDark ? 'rgba(17,17,17,0.98)' : 'rgba(255,255,255,0.98)',
        zIndex: 999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1.5rem',
        transition: 'opacity 0.3s ease, visibility 0.3s ease',
        opacity: menuOpen ? 1 : 0,
        visibility: menuOpen ? 'visible' : 'hidden',
      }}>
        {navLinks.map((link, i) => (
          <button
            key={link.id}
            onClick={() => scrollTo(link.id)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'var(--font-heading)',
              fontWeight: 700,
              fontSize: '1.8rem',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: isDark ? 'white' : 'var(--dark)',
              transition: 'color 0.2s ease',
            }}
            onMouseEnter={(e) => e.target.style.color = 'var(--red)'}
            onMouseLeave={(e) => e.target.style.color = isDark ? 'white' : 'var(--dark)'}
          >
            {link.label}
          </button>
        ))}
      </div>

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btns { display: flex !important; }
        }
      `}</style>
    </>
  );
};

export default Navbar;
